import { ReactNode, useState, useEffect, useRef } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import {
  LogOut, ShieldCheck, Smartphone, Users, Bot, Activity, Coins, ScrollText,
  BarChart3, Sun, Moon, Monitor, LayoutDashboard, Server, Building2, ArrowLeft,
  ChevronDown, Menu, PanelLeft, PlugZap, Settings, Boxes, CreditCard,
  AlertTriangle, BookOpen, CircleDollarSign, Scale, Megaphone, MessagesSquare, DatabaseZap
} from 'lucide-react'
import FlashMessages from '@/frontend/components/FlashMessages'
import UndoToastHost from '@/frontend/components/undo/UndoToastHost'
import { useTheme } from '@/frontend/contexts/ThemeContext'
import { useDropdown } from '@/frontend/hooks/useDropdown'
import CommandPalette from '@/frontend/components/CommandPalette'
import type { SharedProps } from '@/frontend/types/inertia'

interface Props {
  children: ReactNode;
  variant?: 'default' | 'fullscreen';
}

interface NavLinkItem {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface NavItemProps {
  item: NavLinkItem;
  shouldShowExpanded: boolean;
  isMobileMenuOpen: boolean;
  onClick?: () => void;
}

function isPathActive(currentUrl: string, href: string): boolean {
  const path = currentUrl.split('?')[0]
  if (href === '/cc') return path === '/cc'
  return path === href || path.startsWith(href + '/')
}

const NavItem: React.FC<NavItemProps> = ({ item, shouldShowExpanded, isMobileMenuOpen, onClick }) => {
  const { url } = usePage()
  const Icon = item.icon
  const isActive = isPathActive(url, item.href)

  return (
    <li>
      <Link
        href={item.href}
        onClick={onClick}
        title={!shouldShowExpanded ? item.label : ''}
        className={`flex items-center py-2.5 md:py-1.5 transition-colors duration-150 rounded-md ${
          shouldShowExpanded || isMobileMenuOpen ? 'mx-2 px-3' : 'w-full justify-center'
        } ${
          isActive
            ? 'text-foreground bg-accent font-medium'
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
        }`}
      >
        <div className="w-4 h-4 flex-shrink-0">
          <Icon className="h-4 w-4" />
        </div>
        <span className={`text-sm transition-all duration-300 ease-in-out overflow-hidden whitespace-nowrap ${
          !shouldShowExpanded && !isMobileMenuOpen ? 'w-0 opacity-0' : 'ml-3 w-auto opacity-100'
        }`}>
          {item.label}
        </span>
      </Link>
    </li>
  )
}

interface SectionHeaderProps {
  label: string;
  shouldShowExpanded: boolean;
  isMobileMenuOpen: boolean;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ label, shouldShowExpanded, isMobileMenuOpen }) => {
  if (!shouldShowExpanded && !isMobileMenuOpen) {
    return <li className="my-2 mx-3 border-t border-border" aria-hidden="true" />
  }
  return (
    <li className="px-5 pt-4 pb-1">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
    </li>
  )
}

const PAINEL: NavLinkItem = { href: '/cc', label: 'Painel', icon: LayoutDashboard }

const PLATAFORMA: NavLinkItem[] = [
  { href: '/cc/settings', label: 'Settings', icon: Settings },
  { href: '/cc/organizations', label: 'Organizações', icon: Building2 },
  { href: '/cc/users', label: 'Usuários', icon: Users },
  { href: '/cc/broadcasts', label: 'Comunicados', icon: Megaphone },
  { href: '/cc/academy/articles', label: 'Academy', icon: BookOpen },
  { href: '/cc/integrations', label: 'Integrações', icon: PlugZap },
  { href: '/cc/external_oauth_connections', label: 'Credenciais e conexões', icon: PlugZap },
  { href: '/cc/mcp_accesses', label: 'MCP', icon: PlugZap },
  { href: '/cc/wabas', label: 'WABAs', icon: Smartphone },
  { href: '/cc/plans', label: 'Cobrança', icon: CreditCard },
  { href: '/cc/token_plans', label: 'Planos', icon: Coins },
]

const OPERACOES: NavLinkItem[] = [
  { href: '/cc/fleet_machines', label: 'Máquinas', icon: Server },
  { href: '/cc/forge_destinations', label: 'Destinos Forge', icon: DatabaseZap },
  { href: '/cc/incidents', label: 'Incidentes', icon: AlertTriangle },
  { href: '/cc/instances', label: 'Instâncias', icon: Boxes },
  { href: '/cc/digital_workers', label: 'Agentes', icon: Bot },
  { href: '/cc/chat', label: 'Chat', icon: MessagesSquare },
  { href: '/cc/telemetry', label: 'Telemetria', icon: Activity },
  { href: '/cc/litellm_costs', label: 'Custos IA', icon: CircleDollarSign },
  { href: '/cc/eval', label: 'Eval', icon: Scale },
  { href: '/cc/audit_logs', label: 'Auditoria', icon: ScrollText },
  { href: '/cc/panorama_responses', label: 'Panorama', icon: BarChart3 },
]

export default function CommandCenterLayout({ children, variant = 'default' }: Props) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isExpanded, setIsExpanded] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('sidebar-expanded')
      return stored === 'true'
    }
    return true
  })

  const adminPanel = useDropdown()
  const panelRef = useRef<HTMLDivElement>(null)
  const { props: pageProps } = usePage<SharedProps>()
  const auth = pageProps.auth

  const { theme, setTheme } = useTheme()
  // Cycles light -> dark -> system (SZ-1582); the row shows the current theme.
  const nextTheme = { light: 'dark', dark: 'system', system: 'light' } as const
  const themeIcons = { light: Sun, dark: Moon, system: Monitor }
  const themeLabels = { light: 'Tema: Claro', dark: 'Tema: Escuro', system: 'Tema: Sistema' }
  const ThemeIcon = themeIcons[theme]

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen)

  const toggleExpanded = () => {
    setIsExpanded((prev) => {
      const newValue = !prev
      localStorage.setItem('sidebar-expanded', String(newValue))
      return newValue
    })
  }

  const shouldShowExpanded = isExpanded
  const sidebarWidth = shouldShowExpanded ? 'w-52' : isMobileMenuOpen ? 'w-52 md:w-16' : 'md:w-16'

  const prevExpandedRef = useRef(shouldShowExpanded)
  const prevMobileMenuRef = useRef(isMobileMenuOpen)
  useEffect(() => {
    const wasExpanded = prevExpandedRef.current || prevMobileMenuRef.current
    const isNowCollapsed = !shouldShowExpanded && !isMobileMenuOpen
    if (wasExpanded && isNowCollapsed) {
      adminPanel.close()
    }
    prevExpandedRef.current = shouldShowExpanded
    prevMobileMenuRef.current = isMobileMenuOpen
  }, [shouldShowExpanded, isMobileMenuOpen, adminPanel.close])

  useEffect(() => {
    if (adminPanel.isOpen && panelRef.current) {
      const firstFocusable = panelRef.current.querySelector<HTMLElement>('button, [href], input')
      firstFocusable?.focus()
    }
  }, [adminPanel.isOpen])

  useEffect(() => {
    if (!isMobileMenuOpen) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = previousOverflow }
  }, [isMobileMenuOpen])

  const handleLogout = () => {
    adminPanel.close()
    if (confirm('Tem certeza que deseja sair?')) {
      router.delete('/session', { onSuccess: () => setIsMobileMenuOpen(false) })
    }
  }

  const closeMobileMenu = () => setIsMobileMenuOpen(false)

  return (
    <div className="min-h-screen bg-background flex flex-col md:flex-row md:h-screen md:overflow-hidden">
      <CommandPalette />

      {/* Mobile Header */}
      <div className="md:hidden bg-background p-4 flex items-center justify-between border-b border-border">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-red-500" />
          <span className="text-sm font-bold text-foreground">StationZero Admin</span>
        </div>
        {!isMobileMenuOpen && (
          <button
            onClick={toggleMobileMenu}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-accent-foreground hover:bg-accent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring"
            aria-label="Abrir menu"
          >
            <Menu className="h-5 w-5" strokeWidth={1.75} />
          </button>
        )}
      </div>

      {/* Sidebar */}
      <div
        className={`
          ${isMobileMenuOpen ? 'flex fixed' : 'hidden'}
          md:flex md:flex-shrink-0
          flex-col inset-y-0 left-0 h-full md:h-auto
          bg-sidebar border-r border-border
          z-50 transition-all duration-300 ease-in-out
          ${sidebarWidth}
        `}
      >
        {/* Mobile Sidebar Header */}
        <div className="md:hidden p-4 border-b border-border flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-red-500" />
          <span className="text-sm font-bold text-foreground">StationZero Admin</span>
        </div>

        {/* Desktop Header */}
        <div className={`hidden md:flex items-center transition-all duration-300 py-3 ${
          shouldShowExpanded ? 'px-4 md:px-6' : 'justify-center'
        }`}>
          <ShieldCheck className="h-5 w-5 text-red-500 flex-shrink-0" />
          <span className={`text-sm font-bold text-foreground transition-all duration-300 ease-in-out overflow-hidden whitespace-nowrap ${
            shouldShowExpanded ? 'ml-2 w-auto opacity-100' : 'w-0 opacity-0'
          }`}>
            StationZero Admin
          </span>
        </div>

        {/* Main navigation */}
        <div className="flex-1 relative overflow-hidden">
          <nav className="py-1 overflow-y-auto h-full">
            <ul className="space-y-0.5">
              <NavItem
                item={PAINEL}
                shouldShowExpanded={shouldShowExpanded}
                isMobileMenuOpen={isMobileMenuOpen}
                onClick={closeMobileMenu}
              />

              <SectionHeader
                label="Plataforma"
                shouldShowExpanded={shouldShowExpanded}
                isMobileMenuOpen={isMobileMenuOpen}
              />
              {PLATAFORMA.map(item => (
                <NavItem
                  key={item.href}
                  item={item}
                  shouldShowExpanded={shouldShowExpanded}
                  isMobileMenuOpen={isMobileMenuOpen}
                  onClick={closeMobileMenu}
                />
              ))}

              <SectionHeader
                label="Operações"
                shouldShowExpanded={shouldShowExpanded}
                isMobileMenuOpen={isMobileMenuOpen}
              />
              {OPERACOES.map(item => (
                <NavItem
                  key={item.href}
                  item={item}
                  shouldShowExpanded={shouldShowExpanded}
                  isMobileMenuOpen={isMobileMenuOpen}
                  onClick={closeMobileMenu}
                />
              ))}
            </ul>
          </nav>

          {/* Inline admin menu panel — anchored to bottom of nav area */}
          <div
            ref={panelRef}
            data-dropdown
            className={`absolute bottom-0 left-0 right-0 bg-sidebar flex flex-col z-10 max-h-[80%] transition-opacity duration-200 ease-in-out ${
              adminPanel.isOpen && (shouldShowExpanded || isMobileMenuOpen)
                ? 'opacity-100'
                : 'opacity-0 pointer-events-none'
            }`}
            aria-hidden={!adminPanel.isOpen}
          >
            <div className="overflow-y-auto py-2 border-t border-border">
              {auth.user?.email_address && (
                <div className="px-4 py-2">
                  <span className="text-xs text-muted-foreground block truncate">
                    {auth.user.email_address}
                  </span>
                </div>
              )}

              <div className="border-t border-border mt-2 pt-2">
                <Link
                  href="/dashboard"
                  className="flex items-center px-4 py-2 text-foreground hover:bg-accent transition-colors"
                  onClick={() => { adminPanel.close(); closeMobileMenu() }}
                >
                  <ArrowLeft className="h-4 w-4 mr-3" strokeWidth={1.75} />
                  <span>Dashboard</span>
                </Link>

                <button
                  onClick={() => { setTheme(nextTheme[theme]); adminPanel.close() }}
                  title="Mudar tema"
                  className="flex items-center px-4 py-2 text-foreground hover:bg-accent transition-colors w-full"
                >
                  <ThemeIcon className="h-4 w-4 mr-3" strokeWidth={1.75} />
                  <span>{themeLabels[theme]}</span>
                </button>
              </div>

              <div className="border-t border-border mt-2 pt-2">
                <button
                  onClick={handleLogout}
                  className="flex items-center px-4 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors w-full"
                >
                  <LogOut className="h-4 w-4 mr-3" strokeWidth={1.75} />
                  <span>Sair</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom - Admin panel toggle */}
        <div className="border-t border-border mt-auto">
          <button
            ref={adminPanel.buttonRef}
            onClick={() => {
              if (!shouldShowExpanded && !isMobileMenuOpen) {
                setIsExpanded(true)
                localStorage.setItem('sidebar-expanded', 'true')
                setTimeout(() => adminPanel.toggle(), 300)
              } else {
                adminPanel.toggle()
              }
            }}
            className={`w-full flex items-center gap-3 py-3 hover:bg-accent transition-colors ${
              shouldShowExpanded || isMobileMenuOpen ? 'px-4' : 'justify-center'
            }`}
            aria-expanded={adminPanel.isOpen}
            aria-label="Menu administrativo"
          >
            <ShieldCheck className="h-5 w-5 text-red-500 flex-shrink-0" />
            {(shouldShowExpanded || isMobileMenuOpen) && (
              <>
                <span className="flex-1 font-medium text-foreground text-left truncate">
                  Admin
                </span>
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${
                  adminPanel.isOpen ? 'rotate-180' : ''
                }`} />
              </>
            )}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col md:overflow-y-auto">
        <div className="hidden md:flex items-center h-10 px-3 flex-shrink-0">
          <button
            onClick={toggleExpanded}
            className="inline-flex items-center justify-center h-7 w-7 rounded-md text-muted-foreground hover:text-accent-foreground hover:bg-accent transition-colors"
            title={shouldShowExpanded ? 'Recolher sidebar' : 'Expandir sidebar'}
            aria-label={shouldShowExpanded ? 'Recolher sidebar' : 'Expandir sidebar'}
          >
            <PanelLeft className="h-4 w-4" strokeWidth={1.75} />
          </button>
        </div>

        <FlashMessages />
        <UndoToastHost />

        {variant === 'fullscreen' ? (
          <div className="flex-1 overflow-hidden">
            {children}
          </div>
        ) : (
          <div className="flex-1 flex flex-col p-4 md:p-8 relative">
            <div className="absolute inset-0 bg-[linear-gradient(rgba(239,68,68,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(239,68,68,0.02)_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none" />
            <div className="relative z-10 flex-1 w-full max-w-screen-xl mx-auto">
              {children}
            </div>
          </div>
        )}
      </div>

      {/* Mobile menu backdrop overlay */}
      {isMobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/20 z-40"
          onClick={closeMobileMenu}
        />
      )}
    </div>
  )
}
